import DrivePanel from '../components/DrivePanel';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectAPI, taskAPI, userAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiEdit2, FiTrash2, FiPlus, FiUsers, FiCheckSquare, FiClock, FiDollarSign, FiCalendar, FiCheck, FiX, FiSave } from 'react-icons/fi';

const SCOL = { active:{bg:'#d1fae5',txt:'#065f46'}, completed:{bg:'#dbeafe',txt:'#1e40af'}, 'on-hold':{bg:'#fef3c7',txt:'#92400e'}, cancelled:{bg:'#fee2e2',txt:'#991b1b'}, planning:{bg:'#ede9fe',txt:'#5b21b6'} };
const PCOL = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };
const CURRENCY_SYMBOLS = { USD:'$', INR:'₹', EUR:'€', GBP:'£', AED:'د.إ', CAD:'C$', AUD:'A$', SGD:'S$' };
const currSym = (project) => CURRENCY_SYMBOLS[project?.client?.transactionCurrency] || CURRENCY_SYMBOLS[project?.currency] || '$';

export default function ProjectDetail() {
  const { id } = useParams(); const navigate = useNavigate(); const { user } = useAuth();
  const isAdmin  = user?.role === 'admin' || user?.role === 'manager';
  const isClient = user?.role === 'client';
  const [project,setProject]=useState(null); const [tasks,setTasks]=useState([]); const [users,setUsers]=useState([]);
  const [loading,setLoading]=useState(true); const [editing,setEditing]=useState(false);
  const [activeTab,setActiveTab]=useState('overview');
  const [activity,setActivity]=useState([]); const [taskModal,setTaskModal]=useState(false);
  const [memberModal,setMemberModal]=useState(false); const [deleteModal,setDeleteModal]=useState(false);
  const [editForm,setEditForm]=useState({}); const [taskForm,setTaskForm]=useState({title:'',description:'',priority:'medium',dueDate:'',estimatedHours:'',assignedTo:[]});
  const [addingTask,setAddingTask]=useState(false); const [addingMember,setAddingMember]=useState(false);
  const [manualProgress,setManualProgress]=useState(null); // null = use task-based calc
  const [mForm, setMForm] = useState({ title:'', date:'', description:'' });
  const [addingM, setAddingM] = useState(false);
  const [showMForm, setShowMForm] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [updateForm, setUpdateForm] = useState({ content:'', type:'update', timeTakenMinutes:'' });
  const [postingUpdate, setPostingUpdate] = useState(false);

  useEffect(()=>{ load(); },[id]);
  const load = async () => {
    try {
      const [pR,tR,uR] = await Promise.all([projectAPI.getById(id), taskAPI.getAll({project:id}), isAdmin?userAPI.getAll():Promise.resolve({data:{users:[]}})]);
      const proj = pR.data.project||pR.data;
      setProject(proj); setEditForm(proj); setTasks(tR.data.tasks||[]); setUsers(uR.data.users||[]);
      if (proj.progress !== undefined && proj.progress !== null) setManualProgress(proj.progress);
      try { const aR = await projectAPI.getActivity(id); setActivity(aR.data.activity||[]); } catch {}
      try { const uR = await projectAPI.getUpdates(id); setUpdates(uR.data.updates||[]); } catch(e) { console.error('Updates load failed:', e.response?.data?.message || e.message); }
    } catch { toast.error('Failed to load project'); }
    setLoading(false);
  };
  const saveTemplate = async () => {
    const name = prompt('Template name:', project.name + ' Template');
    if (!name) return;
    try {
      const { projectAPI } = await import('../services/api');
      await projectAPI.saveAsTemplate(id, { templateName: name });
      toast.success('Saved as template! View in Project Templates.');
    } catch { toast.error('Failed to save template'); }
  };
  const generateInvoice = async () => {
    try {
      const r = await projectAPI.getInvoice(id);
      const inv = r.data.invoice;
      const { project: p, client, summary, timelogs, tasks } = inv;
      
      // Fetch company settings for logo
      let co = {};
      try { const cr = await companyAPI.get(); co = cr.data.companySettings || {}; } catch {}

      const logoHtml = co.logoBase64
        ? `<img src="${co.logoBase64}" alt="logo" style="max-height:72px;max-width:220px;object-fit:contain;display:block;margin-bottom:6px"/>`
        : `<div class="logo">${co.name || 'ProjectOS'}</div>`;

      // Build printable HTML invoice
      const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Invoice - ${p.name}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #1e293b; padding: 40px; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:40px; border-bottom:3px solid #6366f1; padding-bottom:24px; }
  .logo { font-size:24px; font-weight:900; color:#6366f1; }
  .co-name { font-size:18px; font-weight:800; color:#1e293b; margin-bottom:3px; }
  .co-tag  { font-size:12px; color:#6366f1; margin-bottom:4px; }
  .co-meta { font-size:11px; color:#64748b; line-height:1.6; }
  .invoice-meta { text-align:right; }
  .invoice-meta h2 { font-size:28px; color:#0f172a; }
  .invoice-meta .num { color:#64748b; font-size:13px; margin-top:4px; }
  .parties { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-bottom:32px; }
  .party h3 { font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
  .party .name { font-size:16px; font-weight:700; color:#0f172a; margin-bottom:4px; }
  .party .email { color:#64748b; }
  .kpi-row { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:32px; }
  .kpi { background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:16px; text-align:center; }
  .kpi .val { font-size:22px; font-weight:800; color:#6366f1; }
  .kpi .lbl { font-size:11px; color:#94a3b8; margin-top:4px; text-transform:uppercase; }
  table { width:100%; border-collapse:collapse; margin-bottom:24px; }
  th { background:#f8fafc; padding:10px 12px; text-align:left; font-size:11px; color:#64748b; text-transform:uppercase; border-bottom:2px solid #e2e8f0; }
  td { padding:10px 12px; border-bottom:1px solid #f1f5f9; font-size:13px; }
  .total-row { font-weight:700; background:#f8fafc; }
  .section-title { font-size:15px; font-weight:700; margin:24px 0 12px; color:#0f172a; padding-bottom:8px; border-bottom:1px solid #e2e8f0; }
  .status { padding:3px 10px; border-radius:10px; font-size:11px; font-weight:700; display:inline-block; }
  .completed { background:#d1fae5; color:#065f46; }
  .todo { background:#f1f5f9; color:#475569; }
  .in-progress { background:#fef3c7; color:#92400e; }
  .footer { margin-top:40px; text-align:center; color:#94a3b8; font-size:12px; border-top:1px solid #e2e8f0; padding-top:20px; }
  .highlight { background:var(--pm-gradient); color:#fff; border-radius:12px; padding:20px; margin-bottom:24px; }
  .highlight .amount { font-size:36px; font-weight:900; }
  .highlight .label { opacity:.8; margin-top:4px; }
  @media print { body { padding:20px; } }
</style>
</head>
<body>

<div class="header">
  <div>
    ${logoHtml}
    ${co.name ? `<div class="co-name">${co.name}</div>` : ''}
    ${co.tagline ? `<div class="co-tag">${co.tagline}</div>` : ''}
    <div class="co-meta">
      ${co.address ? co.address + '<br/>' : ''}
      ${[co.city, co.country].filter(Boolean).join(', ')}${co.phone ? ' · ' + co.phone : ''}<br/>
      ${co.email || ''}${co.website ? ' · ' + co.website : ''}
      ${co.gstNumber ? '<br/>GST: ' + co.gstNumber : ''}
    </div>
  </div>
  <div class="invoice-meta">
    <h2>INVOICE</h2>
    <div class="num">INV-${Date.now().toString().slice(-6)}</div>
    <div class="num">Generated: ${new Date().toLocaleDateString('en-IN', {day:'2-digit',month:'long',year:'numeric'})}</div>
    ${p.deadline ? `<div class="num">Project Deadline: ${new Date(p.deadline).toLocaleDateString('en-IN', {day:'2-digit',month:'long',year:'numeric'})}</div>` : ''}
  </div>
</div>

<div class="parties">
  <div class="party">
    <h3>Bill To</h3>
    <div class="name">${client?.name || 'Client'}</div>
    <div class="email">${client?.email || ''}</div>
  </div>
  <div class="party">
    <h3>Project</h3>
    <div class="name">${p.name}</div>
    <div class="email" style="text-transform:capitalize">${p.status} · ${p.description || ''}</div>
  </div>
</div>

<div class="highlight">
  <div class="amount">₹${summary.totalBillable.toLocaleString('en-IN')}</div>
  <div class="label">Total Billable Amount · ${summary.totalHours} hours logged</div>
</div>

<div class="kpi-row">
  <div class="kpi"><div class="val">${summary.taskCount}</div><div class="lbl">Total Tasks</div></div>
  <div class="kpi"><div class="val">${summary.completedTasks}</div><div class="lbl">Completed</div></div>
  <div class="kpi"><div class="val">${summary.totalHours}h</div><div class="lbl">Hours Logged</div></div>
  <div class="kpi"><div class="val">₹${p.budget?.toLocaleString('en-IN') || 0}</div><div class="lbl">Budget</div></div>
</div>

<div class="section-title">Billable Time Logs</div>
<table>
  <thead><tr><th>Date</th><th>Team Member</th><th>Task</th><th>Duration</th><th>Amount</th></tr></thead>
  <tbody>
    ${timelogs.filter(l=>l.isBillable).map(l=>`
    <tr>
      <td>${new Date(l.startTime).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</td>
      <td>${l.user||'—'}</td>
      <td>${l.task||'General'}</td>
      <td>${Math.floor((l.duration||0)/60)}h ${(l.duration||0)%60}m</td>
      <td>₹${(l.billableAmount||0).toLocaleString('en-IN')}</td>
    </tr>`).join('')}
    <tr class="total-row"><td colspan="4">Total Billable</td><td>₹${summary.totalBillable.toLocaleString('en-IN')}</td></tr>
  </tbody>
</table>

<div class="section-title">Task Summary</div>
<table>
  <thead><tr><th>Task</th><th>Priority</th><th>Status</th><th>Est. Hours</th><th>Actual Hours</th></tr></thead>
  <tbody>
    ${tasks.map(t=>`
    <tr>
      <td>${t.title}</td>
      <td style="text-transform:capitalize">${t.priority}</td>
      <td><span class="status ${t.status}">${t.status}</span></td>
      <td>${t.estimatedHours||0}h</td>
      <td>${t.actualHours||0}h</td>
    </tr>`).join('')}
  </tbody>
</table>


${(co.bankName||co.accountNo||co.ifscCode) ? `
<div style="background:#f8fafc;border-radius:10px;padding:16px;margin-top:20px">
  <div style="font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#94a3b8;font-weight:700;margin-bottom:10px">Bank Details for Payment</div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;font-size:12px">
    ${co.bankName  ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:2px">BANK</div><div style="font-weight:700">${co.bankName}</div></div>` : ''}
    ${co.accountNo ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:2px">ACCOUNT NO.</div><div style="font-weight:700">${co.accountNo}</div></div>` : ''}
    ${co.ifscCode  ? `<div><div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:2px">IFSC CODE</div><div style="font-weight:700">${co.ifscCode}</div></div>` : ''}
  </div>
  ${co.paymentTerms ? `<div style="margin-top:10px;font-size:11px;color:#475569;font-style:italic">Payment Terms: ${co.paymentTerms}</div>` : ''}
</div>` : ''}

<div class="footer">
  <p>This invoice was automatically generated from ProjectOS on ${new Date().toLocaleString('en-IN')}</p>
  <p style="margin-top:6px">Thank you for your business!</p>
</div>

</body>
</html>`;
      
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    } catch (e) { toast.error('Failed to generate invoice'); }
  };
  const saveEdit = async () => { try { await projectAPI.update(id,editForm); toast.success('Project updated'); setEditing(false); load(); } catch { toast.error('Update failed'); }};
  const deleteProject = async () => { try { await projectAPI.delete(id); toast.success('Deleted'); navigate('/projects'); } catch { toast.error('Delete failed'); }};
  const addTask = async () => {
    if(!taskForm.title.trim()) return toast.error('Task title is required');
    if(!taskForm.dueDate) return toast.error('Due date is required');
    setAddingTask(true);
    try { await taskAPI.create({...taskForm,project:id}); toast.success('Task added'); setTaskModal(false); setTaskForm({title:'',description:'',priority:'medium',dueDate:'',estimatedHours:'',assignedTo:[]}); load(); }
    catch(e) { toast.error(e.response?.data?.message||'Failed to add task'); }
    setAddingTask(false);
  };
  const addMember = async (uid) => {
    setAddingMember(true);
    try { await projectAPI.addTeamMember(id,{userId:uid}); toast.success('Member added'); load(); }
    catch { toast.error('Failed to add member'); }
    setAddingMember(false);
  };
  const removeMember = async (uid) => { try { await projectAPI.removeTeamMember(id,uid); load(); } catch { toast.error('Failed'); }};
  const toggleTask = async (tid, status) => { try { await taskAPI.update(tid,{status:status==='completed'?'todo':'completed'}); load(); } catch (e) { toast.error(e.response?.data?.message || 'Cannot update task'); } };

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}><div style={{width:40,height:40,border:'3px solid #e2e8f0',borderTop:'3px solid #6366f1',borderRadius:'50%',animation:'spin 1s linear infinite'}} /></div>;
  if (!project) return <div style={{textAlign:'center',padding:60,color:'#94a3b8'}}><p>Project not found</p><Link to="/projects" style={{color:'var(--pm-primary)'}}>← Back</Link></div>;

  const done = tasks.filter(t=>t.status==='completed').length;
  const taskPct = tasks.length ? Math.round((done/tasks.length)*100) : 0;
  const pct = manualProgress !== null ? manualProgress : taskPct;
  const saveProgress = async (val) => {
    try { await projectAPI.update(id, { progress: val }); toast.success('Progress updated'); }
    catch { toast.error('Failed to save progress'); }
  };
  const sc = SCOL[project.status]||SCOL.active;

  const S = {
    page:{maxWidth:1280,margin:'0 auto',fontFamily:"'Sora',sans-serif"},
    topbar:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:28},
    back:{display:'flex',alignItems:'center',gap:8,color:'#64748b',textDecoration:'none',fontWeight:600,fontSize:14},
    hero:{background:'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)',borderRadius:20,padding:'36px 40px',marginBottom:24,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:24,flexWrap:'wrap'},
    title:{margin:0,fontSize:32,fontWeight:800,color:'#f8fafc',letterSpacing:'-0.5px'},
    badge:(bg,txt)=>({background:bg,color:txt,padding:'4px 14px',borderRadius:20,fontSize:12,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}),
    desc:{color:'#94a3b8',lineHeight:1.6,margin:'12px 0 0'},
    ring:{width:100,height:100,position:'relative',flexShrink:0},
    stats:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16,marginBottom:24},
    statCard:c=>({background:'#fff',borderRadius:14,padding:'20px 24px',borderLeft:`4px solid ${c}`,boxShadow:'0 1px 8px #0000000a'}),
    tabs:{display:'flex',gap:4,marginBottom:24,background:'#f1f5f9',borderRadius:10,padding:4},
    tab:(a)=>({padding:'8px 20px',borderRadius:8,border:'none',fontWeight:600,fontSize:14,cursor:'pointer',background:a?'#fff':'transparent',color:a?'var(--pm-primary)':'#64748b',boxShadow:a?'0 1px 4px #0000001a':'none',transition:'all .2s'}),
    card:{background:'#fff',borderRadius:16,padding:24,boxShadow:'0 2px 12px #0000000a',border:'1px solid #f1f5f9'},
    grid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20},
    btn:(v)=>({ padding:'9px 18px',borderRadius:10,border:'none',fontWeight:600,fontSize:13,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,
      ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:
         v==='danger'?{background:'#fee2e2',color:'#ef4444'}:
         {background:'#f8fafc',color:'#64748b',border:'1.5px solid #e2e8f0'}) }),
    overlay:{position:'fixed',inset:0,background:'#00000060',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20},
    modal:{background:'#fff',borderRadius:20,padding:32,width:'100%',maxWidth:520,maxHeight:'85vh',overflowY:'auto'},
    inp:{width:'100%',padding:'9px 13px',border:'2px solid #e2e8f0',borderRadius:9,fontSize:14,outline:'none',boxSizing:'border-box',marginTop:6},
    label:{display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:12},
    taskRow:{display:'flex',alignItems:'center',gap:12,padding:'12px 0',borderBottom:'1px solid #f8fafc'},
  };

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {/* Topbar */}
      <div style={S.topbar}>
        <Link to="/projects" style={S.back}><FiArrowLeft/> Projects</Link>
        {isAdmin && (
          <div style={{display:'flex',gap:8}}>
            {editing ? <>
              <button style={S.btn('ghost')} onClick={()=>setEditing(false)}><FiX/> Cancel</button>
              <button style={S.btn('primary')} onClick={saveEdit}><FiSave/> Save</button>
            </> : <>
              <button style={S.btn('ghost')} onClick={()=>setEditing(true)}><FiEdit2/> Edit</button>
              <button style={{...S.btn('ghost'),borderColor:'var(--pm-primary)',color:'var(--pm-primary)'}} onClick={saveTemplate}>📐 Save as Template</button>
              <button style={{...S.btn('ghost'),borderColor:'#22c55e',color:'#22c55e'}} onClick={generateInvoice}>🧾 Invoice</button>
              <button style={S.btn('danger')} onClick={()=>setDeleteModal(true)}><FiTrash2/></button>
            </>}
          </div>
        )}
      </div>

      {/* Hero */}
      <div style={S.hero}>
        <div style={{flex:1}}>
          {editing ? <input style={{...S.inp,background:'#1e293b',border:'2px solid #334155',color:'#f8fafc',fontSize:28,fontWeight:800}} value={editForm.name||''} onChange={e=>setEditForm({...editForm,name:e.target.value})}/>
            : <h1 style={S.title}>{project.name}</h1>}
          <div style={{display:'flex',gap:10,flexWrap:'wrap',margin:'12px 0',alignItems:'center'}}>
            {isAdmin ? (
              <select
                value={project.status}
                onChange={async e => {
                  try { await projectAPI.update(id, { status: e.target.value }); load(); toast.success('Status updated'); }
                  catch { toast.error('Failed to update status'); }
                }}
                style={{background:sc.bg,color:sc.txt,border:`2px solid ${sc.txt}44`,borderRadius:20,padding:'4px 12px',fontSize:12,fontWeight:700,letterSpacing:1,textTransform:'uppercase',cursor:'pointer',outline:'none'}}>
                {['planning','active','on-hold','completed','cancelled'].map(s=>(
                  <option key={s} value={s} style={{background:'#fff',color:'#0f172a',textTransform:'capitalize'}}>{s}</option>
                ))}
              </select>
            ) : (
              <span style={S.badge(sc.bg,sc.txt)}>{project.status}</span>
            )}
            {project.projectType && <span style={S.badge('#e0f2fe','#0369a1')}>{project.projectType}</span>}
            {project.dueDate && <span style={{color:'#94a3b8',fontSize:13,display:'flex',alignItems:'center',gap:4}}><FiCalendar/> {new Date(project.dueDate).toLocaleDateString()}</span>}
          </div>
          {editing ? <textarea style={{...S.inp,background:'#1e293b',border:'2px solid #334155',color:'#94a3b8',resize:'vertical'}} value={editForm.description||''} onChange={e=>setEditForm({...editForm,description:e.target.value})} rows={3}/>
            : <p style={S.desc}>{project.description||'No description.'}</p>}
        </div>
        <div style={{textAlign:'center',flexShrink:0}}>
          <svg width={100} height={100} viewBox="0 0 100 100">
            <circle cx={50} cy={50} r={40} fill="none" stroke="#334155" strokeWidth={8}/>
            <circle cx={50} cy={50} r={40} fill="none" stroke="var(--pm-primary)" strokeWidth={8}
              strokeDasharray={`${pct*2.513} 251.3`} strokeLinecap="round"
              transform="rotate(-90 50 50)" style={{transition:'stroke-dasharray .8s'}}/>
          </svg>
          <div style={{marginTop:-60,color:'#f8fafc',fontWeight:800,fontSize:22}}>{pct}%</div>
          <div style={{marginTop:32,color:'#64748b',fontSize:12,fontWeight:600}}>COMPLETE</div>
          {isAdmin && (
            <div style={{marginTop:10,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              <input type="range" min={0} max={100} step={5}
                value={manualProgress !== null ? manualProgress : taskPct}
                onChange={e => setManualProgress(Number(e.target.value))}
                onMouseUp={e => saveProgress(Number(e.target.value))}
                onTouchEnd={e => saveProgress(Number(e.target.value))}
                style={{width:100,accentColor:'var(--pm-primary)',cursor:'pointer'}}
                title="Drag to set progress manually"/>
              <span style={{fontSize:10,color:'#475569',fontWeight:600}}>Set Progress</span>
              {manualProgress !== null && (
                <button onClick={async()=>{setManualProgress(null);await projectAPI.update(id,{progress:null});toast.success('Reset to task-based');}}
                  style={{fontSize:10,color:'#94a3b8',background:'none',border:'none',cursor:'pointer',textDecoration:'underline'}}>Reset</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={S.stats}>
        {[{i:<FiCheckSquare/>,l:'Total Tasks',v:tasks.length,c:'var(--pm-primary)'},{i:<FiCheck/>,l:'Done',v:done,c:'#22c55e'},{i:<FiClock/>,l:'In Progress',v:tasks.filter(t=>t.status==='in-progress').length,c:'#f59e0b'},{i:<FiUsers/>,l:'Team',v:project.teamMembers?.length||0,c:'#0ea5e9'},
          ...(isAdmin ? [{i:<span style={{fontWeight:800,fontSize:20}}>{currSym(project)}</span>,l:'Budget',v:`${currSym(project)}${(project.budget||0).toLocaleString()}`,c:'#10b981'}] : [])
        ].map(s=>(
          <div key={s.l} style={S.statCard(s.c)}>
            <div style={{color:s.c,fontSize:22,marginBottom:8}}>{s.i}</div>
            <div style={{fontSize:26,fontWeight:800,color:'#0f172a'}}>{s.v}</div>
            <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {['overview','tasks','gantt','milestones','team',...(isClient?[]:['files']),'updates','activity'].map(t=><button key={t} style={S.tab(activeTab===t)} onClick={()=>setActiveTab(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
      </div>

      {/* Overview */}
      {activeTab==='overview' && (
        <div style={S.grid}>
          <div style={S.card}>
            <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700}}>Details</h3>
            {editing ? (
              <div style={{display:'grid',gap:8}}>
                {[['Status','status','select',['planning','active','on-hold','completed','cancelled']],['Priority','priority','select',['low','medium','high','critical']],['Budget','budget','number'],['Due Date','dueDate','date']].map(([l,k,t,opts])=>(
                  <label key={k} style={{fontSize:13,fontWeight:600,color:'#475569'}}>
                    {l}
                    {t==='select' ? <select style={S.inp} value={editForm[k]||''} onChange={e=>setEditForm({...editForm,[k]:e.target.value})}>{opts.map(o=><option key={o} value={o}>{o}</option>)}</select>
                     : <input style={S.inp} type={t} value={k==='dueDate'&&editForm[k]?editForm[k].slice(0,10):editForm[k]||''} onChange={e=>setEditForm({...editForm,[k]:e.target.value})}/>}
                  </label>
                ))}
              </div>
            ) : (
              <div>{[
                ['Type',project.projectType],
                ['Priority',project.priority],
                ...(isAdmin ? [
                  ['Budget',`${currSym(project)}${(project.budget||0).toLocaleString()}`],
                  ['Spent',`${currSym(project)}${(project.spent||0).toLocaleString()}`],
                  ['Client',project.client?.name||'—'],
                ] : []),
                ['Start',project.startDate?new Date(project.startDate).toLocaleDateString():'—'],
                ['Due',project.dueDate?new Date(project.dueDate).toLocaleDateString():'—'],
                ['Created',new Date(project.createdAt).toLocaleDateString()]
              ].map(([k,v])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid #f8fafc',fontSize:14}}>
                  <span style={{color:'#64748b',fontWeight:500}}>{k}</span>
                  <span style={{fontWeight:600,color:'#0f172a'}}>{v}</span>
                </div>
              ))}</div>
            )}
          </div>
          <div style={S.card}>
            <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700}}>Task Breakdown</h3>
            {[{l:'Completed',c:'#22c55e',s:'completed'},{l:'In Progress',c:'#f59e0b',s:'in-progress'},{l:'In Review',c:'var(--pm-primary)',s:'review'},{l:'Todo',c:'#94a3b8',s:'todo'}].map(x=>(
              <div key={x.l} style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
                <span style={{width:80,fontSize:13,color:'#475569'}}>{x.l}</span>
                <div style={{flex:1,height:8,background:'#f1f5f9',borderRadius:4,overflow:'hidden'}}>
                  <div style={{height:'100%',width:tasks.length?`${(tasks.filter(t=>t.status===x.s).length/tasks.length)*100}%`:'0%',background:x.c,borderRadius:4,transition:'width .6s'}}/>
                </div>
                <span style={{width:24,textAlign:'right',fontSize:13,fontWeight:700,color:x.c}}>{tasks.filter(t=>t.status===x.s).length}</span>
              </div>
            ))}
          </div>
          {isAdmin && project.budget > 0 && (
            <div style={S.card}>
              <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700}}>Budget vs Actual</h3>
              {(() => {
                const sym = currSym(project);
                const budget = parseFloat(project.budget) || 0;
                const spent  = parseFloat(project.spent)  || 0;
                const pct    = budget ? Math.min(100, (spent/budget)*100) : 0;
                const over   = spent > budget;
                const remaining = budget - spent;
                return (
                  <div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:13,fontWeight:600,marginBottom:6}}>
                      <span style={{color:'#64748b'}}>Spent</span>
                      <span style={{color: over ? '#ef4444' : '#0f172a'}}>{sym}{spent.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{height:12,borderRadius:6,background:'#f1f5f9',overflow:'hidden',marginBottom:8}}>
                      <div style={{height:'100%',borderRadius:6,background: over ? '#ef4444' : '#22c55e',width:`${pct}%`,transition:'width .6s'}}/>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#94a3b8',marginBottom:12}}>
                      <span>0</span>
                      <span>{sym}{budget.toLocaleString('en-IN')} budget</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                      {[
                        {l:'Budget',v:`${sym}${budget.toLocaleString('en-IN')}`,c:'#0ea5e9'},
                        {l:'Spent',v:`${sym}${spent.toLocaleString('en-IN')}`,c: over?'#ef4444':'#22c55e'},
                        {l:'Remaining',v:`${sym}${Math.abs(remaining).toLocaleString('en-IN')}`,c: remaining<0?'#ef4444':'#10b981'},
                        {l:'Used',v:`${pct.toFixed(1)}%`,c:'#f59e0b'},
                      ].map(x=>(
                        <div key={x.l} style={{padding:'10px 12px',background:'#f8fafc',borderRadius:10,textAlign:'center'}}>
                          <div style={{fontSize:15,fontWeight:800,color:x.c}}>{x.v}</div>
                          <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{x.l}{x.l==='Remaining'&&remaining<0?' (Over)':''}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Tasks */}
      {activeTab==='tasks' && (
        <div style={S.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Tasks ({tasks.length})</h3>
            {!isClient && <button style={S.btn('primary')} onClick={()=>setTaskModal(true)}><FiPlus/> Add Task</button>}
          </div>
          {tasks.length===0 && <div style={{textAlign:'center',padding:40,color:'#94a3b8'}}>No tasks yet. Add the first one!</div>}
          {tasks.map(t=>(
            <div key={t._id} style={S.taskRow}>
              {!isClient && <div onClick={()=>toggleTask(t._id,t.status)} style={{width:22,height:22,border:`2px solid ${t.status==='completed'?'#22c55e':'#e2e8f0'}`,borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:t.status==='completed'?'#22c55e':'#fff',transition:'all .2s',flexShrink:0}}>
                {t.status==='completed'&&<FiCheck color="#fff" size={13}/>}
              </div>}
              {isClient && <div style={{width:10,height:10,borderRadius:'50%',background:t.status==='completed'?'#22c55e':t.status==='in-progress'?'#f59e0b':'#e2e8f0',flexShrink:0,margin:'0 6px'}}/>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,color:t.status==='completed'?'#94a3b8':'#0f172a',textDecoration:t.status==='completed'?'line-through':'none',fontSize:14}}>{t.title}</div>
                {t.description&&<div style={{fontSize:12,color:'#94a3b8',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t.description}</div>}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:PCOL[t.priority]||'#94a3b8'}} title={t.priority}/>
                {t.dueDate&&<span style={{fontSize:12,color:'#94a3b8'}}>{new Date(t.dueDate).toLocaleDateString()}</span>}
              </div>
              <Link to={`/tasks/${t._id}`} style={{color:'var(--pm-primary)',fontSize:12,fontWeight:600,textDecoration:'none',flexShrink:0}}>View →</Link>
            </div>
          ))}
        </div>
      )}

      {/* Team */}
      {activeTab==='team' && (
        <div style={S.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Team Members</h3>
            {isAdmin&&<button style={S.btn('primary')} onClick={()=>setMemberModal(true)}><FiPlus/> Add Member</button>}
          </div>
          {(!project.teamMembers||project.teamMembers.length===0)
            ? <div style={{color:'#94a3b8',padding:24,textAlign:'center',fontSize:14}}>No members assigned yet. Click "+ Add Member" to get started.</div>
            : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:12}}>
                {(project.teamMembers||[]).map(m=>(
                  <div key={m._id} style={{display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:'#f8fafc',borderRadius:12,border:'1px solid #f1f5f9',transition:'box-shadow .2s'}}>
                    <div style={{width:46,height:46,borderRadius:'50%',background:'var(--pm-gradient)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:18,flexShrink:0}}>{m.name?.charAt(0).toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:14,color:'#0f172a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.name}</div>
                      <div style={{fontSize:12,color:'#64748b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.email}</div>
                      <span style={{display:'inline-block',marginTop:4,fontSize:11,background:'var(--pm-primary)',color:'#fff',padding:'2px 9px',borderRadius:10,fontWeight:600}}>{m.role}</span>
                    </div>
                    {isAdmin&&<button onClick={()=>removeMember(m.id||m.userId)} style={{background:'#fee2e2',border:'none',borderRadius:8,color:'#ef4444',cursor:'pointer',padding:'5px 7px',flexShrink:0}} title="Remove"><FiX size={13}/></button>}
                  </div>
                ))}
              </div>
          }
        </div>
      )}


      {/* Files */}
      {activeTab==='files' && (
        <DrivePanel projectId={id} projectName={project?.name} isAdmin={isAdmin} />
      )}

      {/* Daily Updates */}
      {activeTab==='updates' && (
        <div style={S.card}>
          <h3 style={{margin:'0 0 20px',fontSize:16,fontWeight:700}}>📝 Daily Updates</h3>

          {/* Post update form */}
          {!isClient && (
            <div style={{background:'#f8fafc',borderRadius:12,padding:16,marginBottom:24,border:'1.5px solid #e2e8f0'}}>
              <div style={{display:'flex',gap:8,marginBottom:10}}>
                {[['update','📝 Update'],['blocker','🚧 Blocker'],['milestone','🏁 Milestone']].map(([v,l])=>(
                  <button key={v} onClick={()=>setUpdateForm(f=>({...f,type:v}))}
                    style={{padding:'5px 12px',borderRadius:8,border:`1.5px solid ${updateForm.type===v?'#6366f1':'#e2e8f0'}`,
                      background:updateForm.type===v?'#eef2ff':'#fff',color:updateForm.type===v?'#6366f1':'#64748b',
                      fontWeight:600,fontSize:12,cursor:'pointer'}}>
                    {l}
                  </button>
                ))}
              </div>
              <textarea
                placeholder="What changed today on this project? What did you work on?"
                value={updateForm.content}
                onChange={e=>setUpdateForm(f=>({...f,content:e.target.value}))}
                rows={3}
                style={{width:'100%',padding:'10px 13px',border:'1.5px solid #e2e8f0',borderRadius:9,fontSize:13,resize:'vertical',outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}
              />
              <div style={{display:'flex',alignItems:'center',gap:10,marginTop:8}}>
                <label style={{fontSize:12,color:'#64748b',fontWeight:600,whiteSpace:'nowrap'}}>
                  Time taken (min) <span style={{color:'#ef4444'}}>*</span>
                </label>
                <input
                  type="number" min="1" placeholder="e.g. 30"
                  value={updateForm.timeTakenMinutes}
                  onChange={e=>setUpdateForm(f=>({...f,timeTakenMinutes:e.target.value}))}
                  style={{width:100,padding:'6px 10px',border:`1.5px solid ${updateForm.timeTakenMinutes?'#e2e8f0':'#fca5a5'}`,borderRadius:8,fontSize:13,outline:'none'}}
                />
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
                <button
                  disabled={postingUpdate||!updateForm.content.trim()||!updateForm.timeTakenMinutes||parseInt(updateForm.timeTakenMinutes)<1}
                  onClick={async()=>{
                    if(!updateForm.content.trim()) return toast.error('Please enter update content');
                    if(!updateForm.timeTakenMinutes||parseInt(updateForm.timeTakenMinutes)<1) return toast.error('Time taken is required');
                    setPostingUpdate(true);
                    try {
                      await projectAPI.addUpdate(id, updateForm);
                      setUpdateForm({content:'',type:'update',timeTakenMinutes:''});
                      toast.success('Update posted');
                      load();
                    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
                    setPostingUpdate(false);
                  }}
                  style={{padding:'8px 18px',borderRadius:9,border:'none',background:'var(--pm-gradient)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',opacity:postingUpdate?0.7:1}}>
                  {postingUpdate?'Posting…':'Post Update'}
                </button>
              </div>
            </div>
          )}

          {/* Updates timeline */}
          {updates.length===0
            ? <div style={{color:'#94a3b8',textAlign:'center',padding:32,fontSize:14}}>No updates yet. Post the first daily update above.</div>
            : (() => {
                const grouped = updates.reduce((acc,u)=>{ (acc[u.date]=acc[u.date]||[]).push(u); return acc; },{});
                return Object.entries(grouped).map(([date,items])=>(
                  <div key={date} style={{marginBottom:24}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#94a3b8',letterSpacing:1,marginBottom:10,display:'flex',alignItems:'center',gap:8}}>
                      <div style={{flex:1,height:1,background:'#f1f5f9'}}/>
                      {new Date(date+'T00:00:00').toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short',year:'numeric'})}
                      <div style={{flex:1,height:1,background:'#f1f5f9'}}/>
                    </div>
                    {items.map(u=>{
                      const typeStyle = u.type==='blocker'?{bg:'#fff7ed',border:'#fed7aa',dot:'#f97316',icon:'🚧'}
                        :u.type==='milestone'?{bg:'#f0fdf4',border:'#bbf7d0',dot:'#22c55e',icon:'🏁'}
                        :{bg:'#fafbff',border:'#e0e7ff',dot:'#6366f1',icon:'📝'};
                      const canDelete = isAdmin || u.userId===user?.id || u.author?.id===user?.id;
                      return (
                        <div key={u.id} style={{display:'flex',gap:12,padding:'12px 14px',borderRadius:10,background:typeStyle.bg,border:`1px solid ${typeStyle.border}`,marginBottom:8}}>
                          <div style={{width:8,height:8,borderRadius:'50%',background:typeStyle.dot,flexShrink:0,marginTop:6}}/>
                          <div style={{flex:1}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                              <span style={{fontWeight:700,fontSize:13,color:'#0f172a'}}>{u.author?.name||'Unknown'}</span>
                              <span style={{fontSize:11,background:'#fff',border:'1px solid #e2e8f0',borderRadius:6,padding:'1px 7px',color:'#64748b'}}>{typeStyle.icon} {u.type}</span>
                              <span style={{fontSize:11,color:'#94a3b8',marginLeft:'auto'}}>{new Date(u.createdAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</span>
                              {canDelete && (
                                <button onClick={async()=>{
                                  try{ await projectAPI.deleteUpdate(id,u.id); toast.success('Deleted'); load(); }
                                  catch(e){ toast.error(e.response?.data?.message||'Failed'); }
                                }} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:'0 4px',fontSize:16}} title="Delete">×</button>
                              )}
                            </div>
                            <p style={{margin:0,fontSize:13,color:'#334155',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{u.content}</p>
                            {u.timeTakenMinutes > 0 && (
                              <span style={{display:'inline-block',marginTop:6,fontSize:11,color:'#6366f1',fontWeight:600,background:'#eff0ff',borderRadius:6,padding:'2px 8px'}}>
                                ⏱ {u.timeTakenMinutes} min
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()
          }
        </div>
      )}

      {/* Activity Feed */}
      {activeTab==='activity' && (
        <div style={S.card}>
          <h3 style={{margin:'0 0 20px',fontSize:16,fontWeight:700}}>📋 Project Activity</h3>
          {activity.length===0?<div style={{color:'#94a3b8',textAlign:'center',padding:32,fontSize:14}}>No activity yet</div>:
          <div>{activity.map((a,i)=>(
            <div key={i} style={{display:'flex',gap:12,padding:'10px 0',borderBottom:'1px solid #f8fafc'}}>
              <div style={{width:30,height:30,borderRadius:'50%',background:'var(--pm-gradient)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,flexShrink:0}}>{(a.user?.name||'S').charAt(0)}</div>
              <div style={{flex:1}}>
                <span style={{fontWeight:700,fontSize:13,color:'#0f172a'}}>{a.user?.name||'System'}</span>
                {' '}<span style={{fontSize:13,color:'#64748b'}}>{a.detail||a.action}</span>
              </div>
              <span style={{fontSize:11,color:'#94a3b8',flexShrink:0}}>{new Date(a.createdAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
            </div>
          ))}</div>}
        </div>
      )}

      {/* ── Gantt Chart Tab ───────────────────────────────────────── */}
      {activeTab==='gantt' && (() => {
        const ganttTasks = tasks.filter(t => t.dueDate || t.createdAt);
        const allDates = ganttTasks.flatMap(t => [new Date(t.createdAt), t.dueDate ? new Date(t.dueDate) : new Date()]);
        const minDate = allDates.length ? new Date(Math.min(...allDates)) : new Date();
        const maxDate = allDates.length ? new Date(Math.max(...allDates)) : new Date();
        maxDate.setDate(maxDate.getDate() + 3);
        const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / 86400000));
        const PCOL2 = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };
        const SCOL2 = { completed:'#22c55e', 'in-progress':'#f59e0b', review:'#6366f1', todo:'#94a3b8', blocked:'#ef4444' };
        return (
          <div style={{...S.card,overflowX:'auto'}}>
            <h3 style={{margin:'0 0 20px',fontSize:16,fontWeight:700}}>📅 Gantt Chart</h3>
            {ganttTasks.length === 0 ? <div style={{textAlign:'center',color:'#94a3b8',padding:40}}>No tasks with dates yet</div> : (
              <div style={{minWidth:700}}>
                {ganttTasks.map(t => {
                  const start = new Date(t.createdAt);
                  const end   = t.dueDate ? new Date(t.dueDate) : new Date(start.getTime() + 86400000);
                  const left  = ((start - minDate) / 86400000 / totalDays) * 100;
                  const width = Math.max(1, ((end - start) / 86400000 / totalDays) * 100);
                  const barColor = SCOL2[t.status] || '#94a3b8';
                  return (
                    <div key={t._id} style={{display:'flex',alignItems:'center',marginBottom:10,gap:12}}>
                      <div style={{width:160,fontSize:12,fontWeight:600,color:'#0f172a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flexShrink:0}} title={t.title}>{t.title}</div>
                      <div style={{flex:1,height:28,background:'#f1f5f9',borderRadius:6,position:'relative',overflow:'hidden'}}>
                        <div style={{position:'absolute',top:3,left:`${Math.min(left,98)}%`,width:`${Math.min(width,100-left)}%`,minWidth:6,height:22,background:barColor,borderRadius:4,display:'flex',alignItems:'center',paddingLeft:6,overflow:'hidden',transition:'all .3s'}}>
                          <span style={{fontSize:10,color:'#fff',fontWeight:700,whiteSpace:'nowrap'}}>{t.status}</span>
                        </div>
                      </div>
                      <div style={{width:80,fontSize:11,color:'#64748b',flexShrink:0,textAlign:'right'}}>{t.dueDate?new Date(t.dueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}):'No date'}</div>
                    </div>
                  );
                })}
                <div style={{display:'flex',gap:12,marginTop:16,flexWrap:'wrap'}}>
                  {Object.entries(SCOL2).map(([s,c])=><span key={s} style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'#475569'}}><span style={{width:12,height:12,borderRadius:3,background:c,display:'inline-block'}}/>{s}</span>)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Milestones Tab ────────────────────────────────────────── */}
      {activeTab==='milestones' && (() => {
        const milestones = project.milestones || [];
        const addMilestone = async () => {
          if (!mForm.title.trim()) return toast.error('Title required');
          setAddingM(true);
          try {
            const updated = [...milestones, { id: Date.now(), ...mForm, done: false, createdAt: new Date() }];
            await projectAPI.update(id, { milestones: updated });
            toast.success('Milestone added!'); setShowMForm(false); setMForm({ title:'', date:'', description:'' }); load();
          } catch { toast.error('Failed'); } finally { setAddingM(false); }
        };
        const toggleMilestone = async (mid) => {
          const updated = milestones.map(m => m.id === mid ? { ...m, done: !m.done } : m);
          await projectAPI.update(id, { milestones: updated }); load();
        };
        const deleteMilestone = async (mid) => {
          const updated = milestones.filter(m => m.id !== mid);
          await projectAPI.update(id, { milestones: updated }); load();
        };
        return (
          <div style={S.card}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>🏁 Project Milestones</h3>
              {isAdmin && <button style={S.btn('primary')} onClick={()=>setShowMForm(v=>!v)}><FiPlus/> Add Milestone</button>}
            </div>
            {showMForm && isAdmin && (
              <div style={{background:'#f8fafc',borderRadius:12,padding:16,marginBottom:20,border:'1.5px solid #e2e8f0'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                  <label style={S.label}>Title *<input style={S.inp} value={mForm.title} onChange={e=>setMForm({...mForm,title:e.target.value})} placeholder="Milestone title"/></label>
                  <label style={S.label}>Target Date<input style={S.inp} type="date" value={mForm.date} onChange={e=>setMForm({...mForm,date:e.target.value})}/></label>
                </div>
                <label style={S.label}>Description<input style={S.inp} value={mForm.description} onChange={e=>setMForm({...mForm,description:e.target.value})} placeholder="Optional description"/></label>
                <div style={{display:'flex',gap:8,marginTop:10,justifyContent:'flex-end'}}>
                  <button style={S.btn('ghost')} onClick={()=>setShowMForm(false)}>Cancel</button>
                  <button style={S.btn('primary')} onClick={addMilestone} disabled={addingM}>{addingM?'Saving…':'Save Milestone'}</button>
                </div>
              </div>
            )}
            {milestones.length === 0 ? <div style={{textAlign:'center',color:'#94a3b8',padding:32}}>No milestones yet. Add your first milestone to track key delivery points.</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                {milestones.sort((a,b)=>new Date(a.date||0)-new Date(b.date||0)).map(m => (
                  <div key={m.id} style={{display:'flex',alignItems:'center',gap:14,padding:'14px 16px',borderRadius:12,border:`2px solid ${m.done?'#d1fae5':'#e2e8f0'}`,background:m.done?'#f0fdf4':'#fafbfc',transition:'all .2s'}}>
                    <button onClick={()=>toggleMilestone(m.id)} style={{width:24,height:24,borderRadius:'50%',border:`2px solid ${m.done?'#22c55e':'#d1d5db'}`,background:m.done?'#22c55e':'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all .2s'}}>
                      {m.done && <span style={{color:'#fff',fontSize:12,fontWeight:800}}>✓</span>}
                    </button>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14,color:m.done?'#64748b':'#0f172a',textDecoration:m.done?'line-through':'none'}}>{m.title}</div>
                      {m.description && <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{m.description}</div>}
                    </div>
                    {m.date && <div style={{fontSize:12,fontWeight:600,color:m.done?'#22c55e':new Date(m.date)<new Date()?'#ef4444':'#475569',flexShrink:0}}>{new Date(m.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>}
                    {isAdmin && <button onClick={()=>deleteMilestone(m.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:4}}><FiTrash2 size={14}/></button>}
                  </div>
                ))}
              </div>
            )}
            <div style={{marginTop:16,display:'flex',gap:16,fontSize:13,color:'#64748b'}}>
              <span>✅ {milestones.filter(m=>m.done).length} done</span>
              <span>⏳ {milestones.filter(m=>!m.done).length} pending</span>
            </div>
          </div>
        );
      })()}

      {/* Modals */}
      {taskModal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setTaskModal(false)}>
          <div style={S.modal}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <h3 style={{margin:0}}>Add Task</h3><button style={{background:'none',border:'none',cursor:'pointer',fontSize:22}} onClick={()=>setTaskModal(false)}><FiX/></button>
            </div>
            <label style={S.label}>Title *<input style={S.inp} value={taskForm.title} onChange={e=>setTaskForm({...taskForm,title:e.target.value})} placeholder="Task title"/></label>
            <div style={{marginTop:6}}>
                <div style={{fontSize:13,fontWeight:600,color:'#475569',marginBottom:8}}>Assign To</div>
                <div style={{display:'flex',flexDirection:'column',gap:5,maxHeight:160,overflowY:'auto',border:'2px solid #e2e8f0',borderRadius:9,padding:'8px 10px'}}>
                  {(project.teamMembers||[]).length===0&&<div style={{fontSize:12,color:'#94a3b8'}}>No team members added</div>}
                  {(project.teamMembers||[]).map(m=>{
                    const uid=m.id||m.userId; const name=m.name||m.user?.name;
                    const sel=(taskForm.assignedTo||[]).includes(uid);
                    return <div key={uid} onClick={()=>setTaskForm({...taskForm,assignedTo:sel?(taskForm.assignedTo||[]).filter(x=>x!==uid):[...(taskForm.assignedTo||[]),uid]})}
                      style={{display:'flex',alignItems:'center',gap:8,padding:'5px 8px',borderRadius:7,cursor:'pointer',background:sel?'#eff6ff':'transparent',transition:'all .15s'}}>
                      <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${sel?'var(--pm-primary)':'#e2e8f0'}`,background:sel?'var(--pm-primary)':'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        {sel&&<span style={{color:'#fff',fontSize:9,fontWeight:800}}>✓</span>}
                      </div>
                      <div style={{width:22,height:22,borderRadius:'50%',background:'var(--pm-gradient)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:10,flexShrink:0}}>{name?.charAt(0)}</div>
                      <span style={{fontSize:13,fontWeight:sel?700:400,color:'#0f172a'}}>{name}</span>
                    </div>;
                  })}
                </div>
              </div>
            <label style={S.label}>Description<textarea style={{...S.inp,resize:'vertical'}} value={taskForm.description} onChange={e=>setTaskForm({...taskForm,description:e.target.value})} rows={2}/></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
              <label style={S.label}>Priority<select style={S.inp} value={taskForm.priority} onChange={e=>setTaskForm({...taskForm,priority:e.target.value})}>{['low','medium','high','critical'].map(p=><option key={p} value={p}>{p}</option>)}</select></label>
              <label style={S.label}>Due Date *<input style={{...S.inp, borderColor: !taskForm.dueDate ? '#fca5a5' : undefined}} type="date" required value={taskForm.dueDate} onChange={e=>setTaskForm({...taskForm,dueDate:e.target.value})}/></label>
              <label style={S.label}>Hours<input style={S.inp} type="number" value={taskForm.estimatedHours} onChange={e=>setTaskForm({...taskForm,estimatedHours:e.target.value})} min={0}/></label>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
              <button style={S.btn('ghost')} onClick={()=>setTaskModal(false)} disabled={addingTask}>Cancel</button>
              <button style={{...S.btn('primary'),opacity:addingTask?.6:1}} onClick={addTask} disabled={addingTask}>{addingTask?'Adding…':'Add Task'}</button>
            </div>
          </div>
        </div>
      )}

      {memberModal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setMemberModal(false)}>
          <div style={S.modal}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <h3 style={{margin:0}}>Add Member</h3><button style={{background:'none',border:'none',cursor:'pointer',fontSize:22}} onClick={()=>setMemberModal(false)}><FiX/></button>
            </div>
            {users.filter(u=>u.role!=='client'&&!project.teamMembers?.find(m=>m.id===u.id||m.id===u._id||m.userId===u.id)).map(u=>(
              <div key={u._id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid #f1f5f9'}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'var(--pm-primary)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700}}>{u.name?.charAt(0)}</div>
                <div style={{flex:1}}><div style={{fontWeight:600,fontSize:14}}>{u.name}</div><div style={{fontSize:12,color:'#64748b'}}>{u.email}</div></div>
                <button style={{...S.btn('primary'),opacity:addingMember?.6:1}} onClick={()=>addMember(u._id)} disabled={addingMember}>{addingMember?'Adding…':'Add'}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {deleteModal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setDeleteModal(false)}>
          <div style={{...S.modal,maxWidth:400}}>
            <h3 style={{margin:'0 0 12px'}}>Delete Project?</h3>
            <p style={{color:'#64748b',lineHeight:1.6}}>This will permanently delete <b>{project.name}</b> and all its tasks. Cannot be undone.</p>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:20}}>
              <button style={S.btn('ghost')} onClick={()=>setDeleteModal(false)}>Cancel</button>
              <button style={{...S.btn('danger'),background:'#ef4444',color:'#fff'}} onClick={deleteProject}>Delete Forever</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
